import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { ReactNode, createContext, useCallback, useState } from "react";
import DialogTitle from "@mui/material/DialogTitle";

export type DialogParams = {
    title?: string;                 // dialog title
    message?: string | ReactNode;   // dialog content
    cancelText?: string;            // alternative cancel button text
    confirmText?: string;           // alternative confirm button text
    danger?: boolean;               // make confirm button red
    onCancel: () => void;           // cancel button onClick event handler
    onConfirm: () => void;          // confirm button onClick event handler
};

type ShowConfirmDialogParams = Omit<DialogParams, 'onCancel' | 'onConfirm'>;

interface IConfirmDialogContext {
    showConfirmDialog: (params: ShowConfirmDialogParams) => Promise<boolean>;
}

interface PropTypes {
    children: ReactNode;
}

const ConfirmDialogContext = createContext<IConfirmDialogContext>({
    showConfirmDialog: () => Promise.resolve(false),
});

function ConfirmDialogProvider({ children }: PropTypes) {
    const [dialogParams, setDialogParams] = useState<DialogParams | undefined>(undefined);

    const handleClose = () => {
        // close the dialog
        setDialogParams(undefined);
    };

    const showConfirmDialog = useCallback((params: ShowConfirmDialogParams): Promise<boolean> => {
        return new Promise((resolve) => {
            // set the dialog params
            setDialogParams({
                // det default parameters
                cancelText: 'Cancel',
                confirmText: 'Confirm',
                ...params,
                onCancel: () => {
                    handleClose();
                    resolve(false); // resolve the promise with false if canceled
                },
                onConfirm: () => {
                    handleClose();
                    resolve(true); // resolve the promise with true if confirmed
                },
            });
        });
    }, []);


    return (
        <ConfirmDialogContext.Provider value={{ showConfirmDialog: showConfirmDialog }}>
            <Dialog
                open={dialogParams !== undefined}
                onClose={handleClose}
            >

                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                // pt="16px"
                // px="16px"
                >
                    <DialogTitle variant="h4" align="center">{dialogParams?.title}</DialogTitle>
                </Box>

                <DialogContent>
                    {typeof dialogParams?.message === 'string' && (
                        <DialogContentText align="center" variant="h6">
                            {dialogParams?.message}
                        </DialogContentText>
                    )}
                    {typeof dialogParams?.message !== 'string' && dialogParams?.message}
                </DialogContent>

                <DialogActions>

                    <Button onClick={() => {
                        dialogParams?.onCancel?.();
                        handleClose();
                    }}
                    >
                        {dialogParams?.cancelText ?? ''}
                    </Button>

                    <Button
                        color={dialogParams?.danger ? 'error' : 'primary'}
                        onClick={() => {
                            dialogParams?.onConfirm?.();
                            handleClose();
                        }}
                    >
                        {dialogParams?.confirmText ?? ''}
                    </Button>
                </DialogActions>
            </Dialog>
            {children}
        </ConfirmDialogContext.Provider>
    );
}

export { ConfirmDialogContext, ConfirmDialogProvider };